import { useCallback, useMemo } from 'react'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { useMPAnalytics } from 'apps/marketplace/hooks/useMPAnalytics'
import { useConnectedPhoneNumberContext } from '../ConnectedPhoneNumberContext'
import { useStartPhoneCallMutation } from '../graphql/StartPhoneCallMutation'
import { buildFive9CallUrl } from '../utils'

export interface IUseStartPhoneCallResult {
  canStartPhoneCall: boolean
  startPhoneCall: () => Promise<void>
  /**
   * @description Successfully started phone call using mutation.
   */
  startedPhoneCall: boolean
}

export const useStartPhoneCall = (toPhoneNumber: string): IUseStartPhoneCallResult => {
  const { openToast } = useGlobalToast()
  const { trackEvent } = useMPAnalytics()

  const { userPermissions } = useUserDetails()
  const { canClickToDial = false } = userPermissions ?? {}
  const isAmazonConnectEnabled = useFeatureFlag('amazonConnectClickToCall')
  const canStartPhoneCallWithMutation = isAmazonConnectEnabled && canClickToDial

  const [startPhoneCallMutation, { data }] = useStartPhoneCallMutation()
  const { startPhoneCall: startPhoneCallResult } = data ?? {}
  const { success = false } = startPhoneCallResult ?? {}

  const { five9Data } = useConnectedPhoneNumberContext()
  const { apiURL, campaignName, orgID } = five9Data ?? {}
  const five9Link = useMemo(() => {
    if (apiURL && campaignName && orgID) {
      return buildFive9CallUrl(toPhoneNumber, {
        apiURL,
        campaignName,
        orgID,
      })
    }

    return null
  }, [apiURL, campaignName, orgID, toPhoneNumber])

  const startPhoneCall = useCallback(async () => {
    if (canStartPhoneCallWithMutation) {
      const { data, errors: requestErrors = [] } = await startPhoneCallMutation({
        variables: {
          input: {
            toPhoneNumber,
          },
        },
      })
      const { startPhoneCall } = data ?? {}
      const { errors: serviceErrors = [] } = startPhoneCall ?? {}

      const errorMessages = [
        ...requestErrors.map(({ message }) => message),
        ...serviceErrors.map(({ message }) => message),
      ]

      if (errorMessages.length > 0) {
        openToast({
          toastMessage: 'Failed to start phone call',
          toastType: 'alert',
        })

        trackEvent('Communication', 'ERROR_STARTING_PHONE_CALL', {
          errors: errorMessages,
        })
      }
    } else if (five9Link) {
      window.open(five9Link, '_blank', 'noreferrer')
    }
  }, [
    canStartPhoneCallWithMutation,
    five9Link,
    openToast,
    startPhoneCallMutation,
    toPhoneNumber,
    trackEvent,
  ])

  return {
    canStartPhoneCall: Boolean(five9Link) || canStartPhoneCallWithMutation,
    startPhoneCall,
    startedPhoneCall: success,
  }
}
